import React from 'react';
import { Link } from 'react-router-dom';
import { ClipboardCheck, CheckCircle, Award, TrendingUp, Shield, ArrowRight, AlertTriangle, Scale, Wrench, Settings, RefreshCw } from 'lucide-react';
import { servicesContent } from '../data/mockData';
import SEO from '../components/SEO';

const coreServices = [
  {
    icon: AlertTriangle,
    title: 'Risk Assessment and Hazard Identification',
    description: 'Plant walkthrough to identify accident zones, wear and dust prone zones.'
  },
  {
    icon: Scale,
    title: 'Cost vs Risk Analysis',
    description: 'Compare investment against potential operational loss.'
  },
  {
    icon: Wrench,
    title: 'Custom Design and Manufacturing',
    description: 'Engineered to your machinery and site layout.'
  },
  {
    icon: Shield,
    title: 'Compliance and Safety Standard Alignment',
    description: 'Aligned with industrial safety and ISO norms.'
  },
  {
    icon: Settings,
    title: 'Maintenance Reduction and System Optimization',
    description: 'Lower downtime and longer equipment life cycles.'
  },
  {
    icon: RefreshCw,
    title: 'Safety System Upgrade and Implementation',
    description: 'Replace legacy guards with engineered, compliant systems.'
  }
];

const Services = () => {
  return (
    <div className="min-h-screen bg-[#f9fafb] overflow-hidden">
      <SEO
        title="Industrial Safety & Engineering Services | Stallion Bulktech"
        description="End-to-end industrial services including site assessment, machine guarding design, dust suppression systems, and wear resistance solutions tailored to your plant requirements."
        path="/services"
      />
      {/* Hero Section */}
      <section className="relative pt-24 pb-12 md:pt-32 md:pb-20 overflow-hidden">
        {/* Animated Background Layers */}
        <div className="absolute inset-0 bg-gradient-to-br from-white via-cyan-50/30 to-blue-50/30">
          <div className="absolute top-20 right-20 w-96 h-96 bg-gradient-to-br from-cyan-400/20 to-blue-500/20 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-20 left-20 w-96 h-96 bg-gradient-to-br from-blue-400/20 to-purple-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center space-x-2 px-4 py-2 bg-cyan-50 border border-cyan-200 rounded-full mb-6">
            <ClipboardCheck className="h-4 w-4 text-cyan-600" />
            <p className="text-cyan-600 text-sm font-medium">Our Services</p>
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-6xl font-bold text-gray-900 mb-6">
            {servicesContent.title}
          </h1>
          <p className="text-base md:text-lg text-gray-600 max-w-4xl mx-auto leading-relaxed">
            {servicesContent.description}
          </p>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-12 md:py-20 bg-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-0 left-0 w-64 h-64 bg-cyan-500/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-0 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center space-x-2 px-4 py-2 bg-blue-50 border border-blue-200 rounded-full mb-4">
              <Shield className="h-4 w-4 text-blue-600" />
              <span className="text-blue-600 text-sm font-medium">Our Process</span>
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {servicesContent.processTitle}
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Our end-to-end approach: evaluate, engineer, install, and service
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {servicesContent.process.map((step, index) => (
              <div
                key={step}
                className="group relative h-full"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 to-blue-500/10 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                
                <div className="relative h-full min-h-[100px] bg-white/90 backdrop-blur-xl border border-gray-200 rounded-2xl p-4 hover:border-cyan-300 transition-all duration-500 transform hover:-translate-y-1 hover:shadow-xl">
                  <div className="flex items-center space-x-4 h-full">
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-lg flex items-center justify-center font-bold text-white group-hover:scale-110 transition-transform duration-300 shadow-lg">
                        {index + 1}
                      </div>
                    </div>
                    <div className="flex-1">
                      <p className="text-gray-700 leading-relaxed">{step}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-12 md:py-20 bg-gradient-to-b from-[#f9fafb] to-white relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center space-x-2 px-4 py-2 bg-purple-50 border border-purple-200 rounded-full mb-4">
              <Award className="h-4 w-4 text-purple-600" />
              <span className="text-purple-600 text-sm font-medium">Key Benefits</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Benefits To The Users</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Direct execution from the manufacturer, with measurable impact on machine life and uptime
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {coreServices.map((service, index) => {
              const Icon = service.icon;
              return (
                <div
                  key={service.title}
                  className="group relative h-full"
                  data-testid={`service-card-${index}`}
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/20 to-blue-500/20 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-700" />

                  <div className="relative h-full bg-gradient-to-br from-white to-gray-50/50 border border-gray-200 rounded-2xl p-8 hover:border-cyan-300 transition-all duration-500 transform hover:-translate-y-2 hover:shadow-2xl flex flex-col items-center text-center">
                    {/* Icon — fixed container */}
                    <div className="relative w-14 h-14 mb-6 flex-shrink-0">
                      <div className="absolute inset-0 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl blur opacity-30 group-hover:opacity-60 transition-opacity" />
                      <div className="relative w-14 h-14 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl flex items-center justify-center transform group-hover:scale-110 transition-all duration-500 shadow-lg">
                        <Icon className="h-7 w-7 text-white" />
                      </div>
                    </div>
                    {/* Title — fixed min height (2 lines) */}
                    <h3 className="text-lg md:text-xl font-semibold text-gray-900 mb-3 leading-snug group-hover:text-cyan-600 transition-colors min-h-[3.5rem] flex items-center justify-center">
                      <span className="line-clamp-2">{service.title}</span>
                    </h3>
                    {/* Description — fixed min height (2 lines) */}
                    <p className="text-sm text-gray-600 leading-relaxed min-h-[2.75rem] flex items-center justify-center line-clamp-2">
                      {service.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 relative overflow-hidden">
        {/* Animated background */}
        <div className="absolute inset-0 bg-gradient-to-r from-cyan-600 via-blue-600 to-cyan-600 bg-size-200 animate-gradient" />
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        </div>

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-6 leading-snug md:leading-[1.2]">
            Ready to Engineer Your Machine Guarding, Dust Suppression & Wear Resistance Plan?
          </h2>
          <p className="text-base md:text-xl text-white/90 mb-8 md:mb-10">
            Contact us today to schedule a plant visit
          </p>
          <Link
            to="/contact"
            className="group relative inline-flex items-center space-x-2"
          >
            <div className="absolute inset-0 bg-white rounded-xl blur opacity-50 group-hover:opacity-100 transition-opacity" />
            <div className="relative flex items-center space-x-2 bg-white text-cyan-600 px-10 py-5 rounded-xl font-bold text-lg transform group-hover:scale-105 transition-all shadow-2xl">
              <span>Enquire Now</span>
              <ArrowRight className="h-6 w-6 group-hover:translate-x-2 transition-transform" />
            </div>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Services;
